/**
 * Initialen-Avatar — die einzige Stelle, an der aus einem Namen ein Bildchen
 * wird. Benutzt vom Konto-Menue in der Kopfzeile und vom Avatar-Stack der
 * Presence-Anzeige (Spec 7.1); vorher stand die Initialen-Logik dort doppelt.
 *
 * Die Farbe kommt deterministisch aus dem Namen: derselbe Mensch hat in jeder
 * Sitzung und in jedem Browser denselben Farbton. Damit ist der Stack auf
 * einen Blick lesbar, ohne dass irgendwo eine Farbe gespeichert werden muss.
 *
 * Die Klassennamen stehen bewusst ausgeschrieben im Array — Tailwind scannt
 * den Quelltext, zusammengesetzte Namen wuerden im Build fehlen.
 */
import { cn } from '@/lib/utils';

/**
 * Kraeftige Toene mit weisser Schrift: gleich lesbar in hell und dunkel, und
 * untereinander auch bei Rot-Gruen-Schwaeche unterscheidbar.
 */
const AVATAR_COLORS = [
  'bg-sky-700',
  'bg-emerald-700',
  'bg-violet-700',
  'bg-amber-700',
  'bg-rose-700',
  'bg-teal-700',
  'bg-indigo-700',
  'bg-orange-700',
] as const;

/** Zwei Buchstaben reichen bei einem Support-Team. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  const letters = parts.map((part) => [...part][0]?.toUpperCase() ?? '').join('');
  return letters.length > 0 ? letters : '?';
}

/** Kleine, stabile Streuung (djb2) — kein Krypto, nur eine Farbwahl. */
export function avatarColorOf(seed: string): string {
  let hash = 5381;
  for (let index = 0; index < seed.length; index += 1) {
    hash = ((hash << 5) + hash + seed.charCodeAt(index)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length] ?? AVATAR_COLORS[0];
}

type InitialsAvatarProps = {
  name: string;
  /** Tailwind-Groesse, Default `size-8`. */
  className?: string;
  /**
   * `true`, wenn daneben ohnehin der Name steht — dann ist der Avatar reine
   * Dekoration und darf aus dem Accessibility-Baum verschwinden.
   */
  decorative?: boolean;
};

export function InitialsAvatar({ name, className, decorative = false }: InitialsAvatarProps) {
  return (
    <span
      className={cn(
        'inline-flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-medium text-white',
        avatarColorOf(name),
        className,
      )}
      {...(decorative
        ? { 'aria-hidden': true }
        : { role: 'img', 'aria-label': name, title: name })}
    >
      {initialsOf(name)}
    </span>
  );
}
